import * as React from "react";

import { getLinkedinChanges } from "../api/linkedin-agent";
import { DataTablePagination } from "../components/data-table";
import { LinkedinChange } from "../types/linkedin";
import { useLoading } from "./loading";

const PAGE_SIZE = 15;

export interface LinkedinChangesFeed {
  rows: LinkedinChange[];
  total: number;
  isLoading: boolean;
  pagination: DataTablePagination;
}

// A cursor-paged feed of an ad account's (or one campaign's) changes.
// Pages are cached as they're fetched, so stepping back is free; bumping
// `version` throws the cache away and starts over from the first page.
export const useLinkedinChanges = (
  workspace: string,
  adAccountId: number,
  { campaignId, version = 0 }: { campaignId?: number; version?: number } = {},
): LinkedinChangesFeed => {
  const [pages, setPages] = React.useState<LinkedinChange[][]>([]);
  const [cursors, setCursors] = React.useState<(string | null)[]>([]);
  const [page, setPage] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [isLoading, withLoading] = useLoading();
  // A first page opens a new generation, so a page of the previous scope
  // that lands late can't overwrite the current one.
  const generation = React.useRef(0);

  const fetchPage = React.useCallback(
    (cursor: string | undefined, index: number) => {
      if (index === 0) generation.current += 1;
      const current = generation.current;
      withLoading(async () => {
        const data = await getLinkedinChanges(workspace, adAccountId, {
          campaignId,
          cursor,
          perPage: PAGE_SIZE,
        });
        if (current !== generation.current) return;
        setPages((prev) => {
          const next = prev.slice(0, index);
          next[index] = data.items;
          return next;
        });
        setCursors((prev) => {
          const next = prev.slice(0, index);
          next[index] = data.meta.cursor;
          return next;
        });
        setTotal(data.meta.total);
        setPage(index);
      }).catch(() => {});
    },
    [adAccountId, campaignId, withLoading, workspace],
  );

  // fetchPage with index 0 truncates the cached pages/cursors itself, so
  // a change of scope or version only needs to kick off the first fetch.
  React.useEffect(() => {
    fetchPage(undefined, 0);
  }, [fetchPage, version]);

  const onPageChange = (next: number) => {
    if (isLoading || next < 0) return;
    if (next < pages.length && pages[next]) {
      setPage(next);
      return;
    }
    const lastFetched = pages.length - 1;
    const cursor = cursors[lastFetched];
    if (next === lastFetched + 1 && cursor != null) {
      fetchPage(cursor, next);
    }
  };

  return {
    rows: pages[page] ?? [],
    total,
    isLoading,
    pagination: {
      page,
      pageSize: PAGE_SIZE,
      total,
      onPageChange,
      sequential: true,
    },
  };
};
